import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { Keywords } from 'src/app/models/walai';
import { ConfigService } from 'src/app/services/config.service';
import { HomeService } from 'src/app/services/home.service';
import { SearchService } from 'src/app/services/search.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-book-cover-share',
  templateUrl: './book-cover-share.component.html',
  styleUrls: ['./book-cover-share.component.css'],
})
export class BookCoverShareComponent implements OnInit {
  @Input() bookCoverURL: string = '';
  @Input() id: string = '';
  @Input() cssStyle: string = '';
  @Input() title: string = '';

  isLoading: boolean = true;
  isCover: boolean = false;
  isCustomNoImage: boolean = false;
  bgClass: string = '';
  cover: string = 'assets/images/bookcover/loading.jpg';
  noimg: string = 'assets/images/bookcover/noimage.jpg';

  constructor(
    private searchService: SearchService,
    private fc: UtilityService,
    private homeS: HomeService,
    private configService: ConfigService,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    this.selectBgColor();
    this.loadCover();
  }

  // --- Public Event Handlers ---

  public imagerror(e: Event): void {
    if (this.cover === this.bookCoverURL && this.bookCoverURL && this.bookCoverURL !== this.noimg) {
      this.bookCoverURL = '';
      this.keywordSearch();
    } else {
      this.setNoImage();
      const target = e.target as HTMLImageElement;
      target.src = this.noimg;
      target.onerror = null;
    }
  }

  public imageLoaded(e: Event): void {
    const target = e.target as HTMLImageElement;
    if (target.naturalWidth <= 1 && target.naturalHeight <= 1) {
      this.imagerror(e);
    }
  }

  // --- Core Loading Logic ---

  private async loadCover(): Promise<void> {
    this.isLoading = true;
    try {
      if (this.id?.includes('j')) {
        this.homeS.bookcover(this.id).subscribe(
          res => this.setCover(res),
          () => this.setNoImage()
        );
      } else if (this.bookCoverURL) {
        this.setCover(this.bookCoverURL, true);
      } else {
        this.keywordSearch();
      }
    } catch {
      this.setNoImage();
    }
  }

  private keywordSearch(): void {
    if (!this.id) return this.setNoImage();

    this.isLoading = true;
    const keyword = new Keywords();
    keyword.CurrentPage = 1;
    keyword.RowPerPage = 1;
    keyword.Ntk = 'ID';
    keyword.Ntt = this.id;
    keyword.Orderby = 'RELEVANCE';

    this.searchService.keywordsearch(keyword).subscribe(
      (results: any) => {
        const item = results?.Display?.[0];
        if (!item) return this.setNoImage();

        const useWalai = this.configService.useWalaiBookCover === '1';
        const isWU = this.configService.siteName === 'WU' || this.configService.siteName === 'DEFAULT';

        if (useWalai && isWU) {
          this.fetchWalaiCover(item.ISBNISSN, results);
        } else {
          this.fetchLangCover(item);
        }
      },
      () => this.setNoImage()
    );
  }

  private fetchWalaiCover(isbn: string, results: any): void {
    const isbn13 = this.fc.getISBN(isbn);
    if (!isbn13) return this.fetchLangCover(results.Display[0]);

    const apiUrl = `https://opac-api.wu.ac.th/synccoverbook/v1/api/GetBookCover/${isbn13}`;
    this.http.get(apiUrl, { responseType: 'text' }).subscribe({
      next: (uri) => {
        if (!uri || uri === 'Invalid ISBN' || uri === 'ไม่พบเลข ISBN') {
          this.fetchLangCover(results.Display[0]);
        } else {
          this.setCover(uri);
        }
      },
      error: () => this.fetchLangCover(results.Display[0])
    });
  }

  private fetchLangCover(item: any): void {
    const url = item.LANG === 'ภาษาไทย'
      ? this.fc.getBookTha(item.ISBNISSN)
      : this.fc.getBookEng(item.ISBNISSN, this.noimg);
    this.setCover(url);
  }

  private async setCover(url: string, optimistic: boolean = false): Promise<void> {
    if (!url || this.isUrlDefaultImage(url)) {
      return this.setNoImage();
    }

    if (optimistic) {
      this.cover = url;
      this.isCover = true;
      this.isLoading = false;
      return;
    }

    const validUrl = await this.checkImageSize(url);
    this.cover = validUrl;
    this.isCover = validUrl !== this.noimg;
    this.isLoading = false;
  }

  private setNoImage(): void {
    this.cover = this.noimg;
    this.isCover = false;
    this.isLoading = false;
  }

  // --- Helpers ---

  private async checkImageSize(url: string): Promise<string> {
    if (this.isUrlDefaultImage(url)) return url;
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve((img.naturalWidth <= 1 && img.naturalHeight <= 1) ? this.noimg : url);
      img.onerror = () => resolve(this.noimg);
      img.src = url;
    });
  }

  private isUrlDefaultImage(url: string): boolean {
    return url.includes('assets/images/bookcover') || url.includes('/noimage.jpg');
  }

  private selectBgColor(): void {
    if (this.configService.noImage === 'multiple') {
      const palette = this.configService.bookCoverColorPalette || 'pastel';
      const num = this.id ? (parseInt(this.id.toString().slice(-1), 10) || 0) + 1 : 1;
      this.bgClass = `bg-${palette}-${num}`;
    } else if (this.configService.noImage === 'custom') {
      const folder = this.configService.noImageFolder;
      if (folder) {
        const index = this.id ? (parseInt(this.id.toString().slice(-1), 10) || 0) : 0;
        this.noimg = `assets/images/bookcover/${folder}/${index}.JPG`;
        this.isCustomNoImage = true;
      }
    }
  }
}
